import { useState } from 'react';
import { flagMinuteLabel } from '../../app/flags';
import type { FlagRow } from '../../app/flags';
import { PHASE_OPTIONS } from '../../ui/halftime/phaseOptions';
import { useFlags } from '../../ui/halftime/useFlags';
import { PhaseIcon } from '../PhaseIcon';
import { FlagPlaybackB } from './FlagPlaybackB';
import { flagsEmptyCopyB, slotFreeEntriesB } from './flagsReviewB';
import type { HypothesisPhase, MatchRow, TemplateEntry } from '../../types';

/**
 * B-13 — **the flags review** (design spec §2.4; scenario 15; requirements §3.2.15): the
 * unresolved queue minute-ascending, voice-note playback with honest captions, and triage per
 * flag — dismiss / attach to a hypothesis / promote to a new one (`ResolveFlag`'s three plans).
 *
 * ── A middle-band surface, deliberately NOT a dialog ────────────────────────
 *
 * §2.4 lists the flags review beside the capture surfaces in the middle band, and the LockReview
 * reasoning binds here identically: the whistle in the top band must stay reachable — a dead
 * spell ends when the game says so, and the Half hold must never be inert behind a sheet. The
 * shell renders this INSTEAD of the capture surface and drops it the moment the state-driven
 * default moves (a committed transition wins every disagreement with navigation).
 *
 * ── Nothing here decides anything ───────────────────────────────────────────
 *
 * The controller is A's own `useFlags` hook, imported whole (the `useCaptureClock` /
 * `useRecorderView` / `PHASE_OPTIONS` precedent — a mirrored B controller would be exactly the
 * near-duplicate the reuse contract's detector exists to catch). Through it: the queue is
 * `loadFlagsContext`'s — derived by `listUnresolvedFlags` (flags minus resolutions), already
 * `(matchMinute asc, createdAt asc)`, so nothing is sorted here and "a resolved flag never
 * reappears" is the `&observationId` unique index's fact, not this component's memory. Every
 * resolution re-reads the derived queue rather than pruning locally (a local removal would be a
 * second representation of "resolved"). Minute labels are `flagMinuteLabel`'s — the
 * `stateAt`-derived HT discriminator, never a `=== halfLengthMin` guess. Promote ids are minted
 * inside the hook at the call site (`capture-spawning-instance-handle-at-creation` — inherited,
 * not re-implemented).
 *
 * ── Every triage path is taps alone ─────────────────────────────────────────
 *
 * Attach (a pattern that exists), a slot-free library entry (real text + bucket), or the
 * auto-label `Flagged 34′` with a tapped phase. No free text exists on this surface (the
 * engine has no hypothesis-rename command — the auto-label is the pattern's name), and B-7's
 * convention holds: the phase is a required pick with **no default** (a default would file
 * every promoted flag on one Discovery page).
 */
export interface FlagsReviewSurfaceBProps {
  /** The SHELL's authoritative row — re-read after every committed transition (B-10's rule). */
  readonly match: MatchRow;
  /** Done — the shell re-reads (a promoted pattern must reach the remounted surface) and closes. */
  readonly onClose: () => void;
}

export function FlagsReviewSurfaceB({ match, onClose }: FlagsReviewSurfaceBProps) {
  const { state: flags, actions } = useFlags(match.id);
  const [promoting, setPromoting] = useState<FlagRow | null>(null);
  /** B-7's convention: required, no default — Create stays inert until a phase is tapped. */
  const [phase, setPhase] = useState<HypothesisPhase | null>(null);

  /**
   * The promote pane exists only while its flag is still queued — the same derivation A's sheet
   * uses: the queue is re-read after every write, so a resolution (this pane's own, or a
   * double-tap's rejection elsewhere) lands the user back on the queue instead of on a pane for
   * a flag that no longer needs one.
   */
  const promotingStillQueued =
    promoting !== null &&
    (flags.context?.unresolved.some((row) => row.observationId === promoting.observationId) ??
      true);
  const view = promotingStillQueued ? 'promote' : 'queue';
  const unresolved = flags.context?.unresolved ?? [];

  const closePromote = (): void => {
    setPromoting(null);
    setPhase(null);
  };

  return (
    <div
      className="flb"
      data-testid="flb"
      data-view={view}
      data-phase={flags.phase}
      data-count={String(unresolved.length)}
      role="region"
      aria-label="Flags review"
    >
      <div className="flb-bar">
        <h2 className="flb-title" data-testid="flb-title">
          {view === 'promote' ? 'Promote this flag' : `Flags (${String(unresolved.length)})`}
        </h2>
        {view === 'promote' && (
          <button
            type="button"
            className="flb-secondary"
            data-testid="flb-back"
            onClick={closePromote}
          >
            Back
          </button>
        )}
        <button type="button" className="flb-secondary" data-testid="flb-done" onClick={onClose}>
          Done
        </button>
      </div>

      <div className="flb-body">
        {flags.notice !== null && (
          <p className="flb-notice" role="status" data-testid="flb-notice">
            {flags.notice}
            <button type="button" className="flb-linkish" onClick={actions.dismissNotice}>
              Dismiss
            </button>
          </p>
        )}

        {/* The resolution acknowledgment: which pattern the flag now lives under. A's board
            offer is deliberately absent — placing a pattern is the break hub's board manage
            (B-14; `SwapBoardTile` is halftime-only), and §2.4's B-13 lists the three plans. */}
        {view === 'queue' && flags.justPromoted !== null && (
          <p className="flb-filed" role="status" data-testid="flb-filed">
            Filed under — <strong>{flags.justPromoted.text}</strong>
            <button
              type="button"
              className="flb-linkish"
              data-testid="flb-filed-ok"
              onClick={actions.clearPromoted}
            >
              OK
            </button>
          </p>
        )}

        {flags.phase === 'loading' && <p className="flb-empty">Loading…</p>}
        {flags.phase === 'failed' && (
          <p className="flb-empty" role="alert" data-testid="flb-failed">
            The flags could not be loaded. The log has the detail.
          </p>
        )}

        {flags.phase === 'ready' &&
          (view === 'promote' && promoting !== null ? (
            <PromotePaneB
              flag={promoting}
              match={match}
              flags={flags}
              phase={phase}
              onPhase={setPhase}
              onAttach={(hypothesisId, text) => {
                void actions.attach(promoting.observationId, hypothesisId, text).then((done) => {
                  if (done) closePromote();
                });
              }}
              onTemplate={(templateId, entry) => {
                void actions
                  .promoteFromTemplate(promoting.observationId, templateId, entry)
                  .then((done) => {
                    if (done) closePromote();
                  });
              }}
              onAutoLabel={(picked) => {
                void actions
                  .promoteWithAutoLabel(promoting.observationId, promoting.matchMinute, picked)
                  .then((done) => {
                    if (done) closePromote();
                  });
              }}
            />
          ) : (
            <QueueB
              rows={unresolved}
              match={match}
              busy={flags.busy}
              onPromote={setPromoting}
              onDismiss={(observationId) => {
                void actions.dismiss(observationId);
              }}
            />
          ))}
      </div>
    </div>
  );
}

// ── The queue ────────────────────────────────────────────────────────────────

interface QueueBProps {
  readonly rows: readonly FlagRow[];
  readonly match: MatchRow;
  readonly busy: boolean;
  readonly onPromote: (row: FlagRow) => void;
  readonly onDismiss: (observationId: string) => void;
}

/**
 * DIST-1's queue, rendered as delivered — `(matchMinute asc, createdAt asc)` is
 * `listUnresolvedFlags`'s order and nothing is sorted here. §8.2's honesty per row: a flag
 * whose audio cannot play still shows its minute (the moment survives without the recording),
 * and the caption says which kind of nothing this is (silent flag / audio unavailable) — a Play
 * control renders only over bytes the store actually holds.
 */
function QueueB({ rows, match, busy, onPromote, onDismiss }: QueueBProps) {
  if (rows.length === 0) {
    return (
      <p className="flb-empty" data-testid="flb-empty">
        {flagsEmptyCopyB(match.state)}
      </p>
    );
  }

  return (
    <ul className="flb-list" data-testid="flb-list">
      {rows.map((row) => (
        <li
          className="flb-flag"
          key={row.observationId}
          data-testid={`flb-flag-${row.observationId}`}
          data-minute={String(row.matchMinute)}
        >
          {/* `flagMinuteLabel` — the stateAt-derived label ("HT · 30′" only for a flag created
              AT halftime; a first-minute-of-2H flag renders without HT). The match row satisfies
              `MatchAnchors` structurally. */}
          <span className="flb-when" data-testid="flb-when">
            {flagMinuteLabel(match, row)}
          </span>

          <span className="flb-flag__audio">
            {row.audio.available && row.audio.audioBlobId !== null ? (
              <FlagPlaybackB
                audioBlobId={row.audio.audioBlobId}
                label={`the ${String(row.matchMinute)}′ flag`}
              />
            ) : (
              <span className="flb-silent" data-testid="flb-silent">
                {row.audio.caption}
              </span>
            )}
          </span>

          <span className="flb-flag__actions">
            <button
              type="button"
              className="flb-act"
              data-testid={`flb-promote-${row.observationId}`}
              disabled={busy}
              onClick={() => {
                onPromote(row);
              }}
            >
              Promote
            </button>
            <button
              type="button"
              className="flb-act"
              data-testid={`flb-dismiss-${row.observationId}`}
              disabled={busy}
              onClick={() => {
                onDismiss(row.observationId);
              }}
            >
              Dismiss
            </button>
          </span>
        </li>
      ))}
    </ul>
  );
}

// ── The promote pane ─────────────────────────────────────────────────────────

interface PromotePaneBProps {
  readonly flag: FlagRow;
  readonly match: MatchRow;
  readonly flags: ReturnType<typeof useFlags>['state'];
  readonly phase: HypothesisPhase | null;
  readonly onPhase: (phase: HypothesisPhase) => void;
  readonly onAttach: (hypothesisId: string, text: string) => void;
  readonly onTemplate: (templateId: string, entry: TemplateEntry) => void;
  readonly onAutoLabel: (phase: HypothesisPhase) => void;
}

/** §10.1's three paths in their order; every one completes with taps alone. */
function PromotePaneB({
  flag,
  match,
  flags,
  phase,
  onPhase,
  onAttach,
  onTemplate,
  onAutoLabel,
}: PromotePaneBProps) {
  const attachable = flags.context?.attachable ?? [];
  const offers = slotFreeEntriesB(flags.context?.library ?? []);
  const busy = flags.busy;

  return (
    <div className="flb-promote" data-testid="flb-promote">
      {/* The flag stays visible and playable throughout — deciding what a flag was about
          without being able to hear it again is the one thing this pane must not ask. */}
      <div className="flb-promote__flag">
        <span className="flb-when">{flagMinuteLabel(match, flag)}</span>
        {flag.audio.available && flag.audio.audioBlobId !== null ? (
          <FlagPlaybackB audioBlobId={flag.audio.audioBlobId} label="it again" />
        ) : (
          <span className="flb-silent">{flag.audio.caption}</span>
        )}
      </div>

      {/* (a) — attach to a pattern that already exists. Absent when there is none, rather than
          rendered empty (an empty list under a heading reads as a list that failed to load). */}
      {attachable.length > 0 && (
        <section className="flb-section">
          <h3 className="flb-section__title">Add it to a pattern you already have</h3>
          <ul className="flb-picklist" data-testid="flb-attach">
            {attachable.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className="flb-pick"
                  data-testid={`flb-attach-${row.id}`}
                  disabled={busy}
                  onClick={() => {
                    onAttach(row.id, row.text);
                  }}
                >
                  <span className="flb-pick__text">{row.text}</span>
                  {row.onBoard && <span className="flb-pick__note">on the board</span>}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* (b) — a slot-free library entry: real text and a discoveryBucket. */}
      {offers.length > 0 && (
        <section className="flb-section">
          <h3 className="flb-section__title">Or start one from the library</h3>
          <ul className="flb-picklist" data-testid="flb-templates">
            {offers.map(({ templateId, templateName, entry }) => (
              <li key={`${templateId}:${entry.text}`}>
                <button
                  type="button"
                  className="flb-pick"
                  disabled={busy}
                  onClick={() => {
                    onTemplate(templateId, entry);
                  }}
                >
                  <PhaseIcon phase={entry.phase} />
                  <span className="flb-pick__text">{entry.text}</span>
                  <span className="flb-pick__note">{templateName}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* (c) — the path that always exists: the auto-label plus a tapped phase (required, no
          default — B-7's convention; a default would file every promoted flag on one page). */}
      <section className="flb-section">
        <h3 className="flb-section__title">Or name it after the flag</h3>
        <p className="flb-auto" data-testid="flb-auto-label">
          It will be called <strong>Flagged {String(flag.matchMinute)}′</strong> — enough to find
          the moment again after the match.
        </p>
        <div className="flb-phases" role="group" aria-label="Which phase is this about?">
          {PHASE_OPTIONS.map((option) => (
            <button
              key={option.phase}
              type="button"
              className="flb-phase"
              data-testid={`flb-phase-${option.phase}`}
              aria-pressed={phase === option.phase}
              onClick={() => {
                onPhase(option.phase);
              }}
            >
              <PhaseIcon phase={option.phase} />
              {option.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="flb-create"
          data-testid="flb-create"
          disabled={busy || phase === null}
          onClick={() => {
            if (phase !== null) onAutoLabel(phase);
          }}
        >
          Create it
        </button>
      </section>
    </div>
  );
}
